import type { Category } from '@repo/sdk'

export interface CategoryTreeNode {
  value: string
  title: string
  children?: CategoryTreeNode[]
}

/**
 * Builds a TreeSelect-compatible node tree from a flat category list using `parentId`.
 * Every node stays selectable so a dealer can stop at depth 1, 2 or 3.
 * Categories whose parent is missing from the list are treated as roots so nothing is dropped.
 */
export function buildCategoryTreeNodes(categories: readonly Category[]): CategoryTreeNode[] {
  const sorted = [...categories].sort((a, b) => a.displayOrder - b.displayOrder)
  const nodeById = new Map<string, CategoryTreeNode>(
    sorted.map((category) => [category.id, { value: category.id, title: category.name }]),
  )

  const roots: CategoryTreeNode[] = []

  for (const category of sorted) {
    const node = nodeById.get(category.id)
    if (!node) continue

    const parent = category.parentId ? nodeById.get(category.parentId) : undefined
    if (!parent || parent === node) {
      roots.push(node)
      continue
    }

    parent.children = [...(parent.children ?? []), node]
  }

  return roots
}
